import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  fetchTemplateTypesList,
  fetchCategoryList,
} from '@/services/tenant/template-services';
import { RootState } from '@/store';

export interface TemplateType {
  id: number;
  name: string;
  [key: string]: any;
}

export interface Category {
  id: number;
  name: string;
  [key: string]: any;
}

interface TemplateState {
  types: TemplateType[];
  isLoadingTypes: boolean;
  typesError: string | null;
  categories: Category[];
  isLoadingCategories: boolean;
  categoriesError: string | null;
}

const initialState: TemplateState = {
  types: [],
  isLoadingTypes: false,
  typesError: null,
  categories: [],
  isLoadingCategories: false,
  categoriesError: null,
};

export const fetchTemplateTypesThunk = createAsyncThunk<
  TemplateType[],
  void,
  { state: RootState; rejectValue: string }
>('template/fetchTemplateTypes', async (_, { rejectWithValue }) => {
  try {
    const data = await fetchTemplateTypesList();
    return data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch template types');
  }
});

export const fetchCategoriesThunk = createAsyncThunk<
  Category[],
  void,
  { state: RootState; rejectValue: string }
>('template/fetchCategories', async (_, { rejectWithValue }) => {
  try {
    const data = await fetchCategoryList();
    return data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch categories');
  }
});

const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    clearTemplateTypes: state => {
      state.types = [];
      state.typesError = null;
    },
    clearCategories: state => {
      state.categories = [];
      state.categoriesError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTemplateTypesThunk.pending, state => {
        state.isLoadingTypes = true;
        state.typesError = null;
      })
      .addCase(
        fetchTemplateTypesThunk.fulfilled,
        (state, action: PayloadAction<TemplateType[]>) => {
          state.isLoadingTypes = false;
          state.types = action.payload;
        }
      )
      .addCase(fetchTemplateTypesThunk.rejected, (state, action) => {
        state.isLoadingTypes = false;
        state.typesError = action.payload ?? 'Unknown error';
      })
      .addCase(fetchCategoriesThunk.pending, state => {
        state.isLoadingCategories = true;
        state.categoriesError = null;
      })
      .addCase(
        fetchCategoriesThunk.fulfilled,
        (state, action: PayloadAction<Category[]>) => {
          state.isLoadingCategories = false;
          state.categories = action.payload;
        }
      )
      .addCase(fetchCategoriesThunk.rejected, (state, action) => {
        state.isLoadingCategories = false;
        state.categoriesError = action.payload ?? 'Failed to fetch categories';
      });
  },
});

export const { clearTemplateTypes, clearCategories } = templateSlice.actions;

export const selectTemplateTypes = (state: RootState) =>
  state.tenant.template.types;
export const selectTemplateTypesLoading = (state: RootState) =>
  state.tenant.template.isLoadingTypes;
export const selectTemplateTypesError = (state: RootState) =>
  state.tenant.template.typesError;

export const selectCategories = (state: RootState) =>
  state.tenant.template.categories;
export const selectCategoriesLoading = (state: RootState) =>
  state.tenant.template.isLoadingCategories;
export const selectCategoriesError = (state: RootState) =>
  state.tenant.template.categoriesError;

export default templateSlice.reducer;
